import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Column, Row } from "@/shared/ui/Blocks";
import { Label } from "@/shared/ui/Label";
import { SubtitleText } from "@/shared/ui";
import {
  AssessmentSurveyGroup,
  AssessmentSurveyType,
} from "@/shared/constants/assessmentSurveys";
import { StatusBadge } from "@/shared/ui/StatusBadge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/Tooltip";
import { cn } from "@/shared/utils/shadcn";
import { Icon, IconName } from "@/shared/ui/Icon";
import { filter, map } from "lodash";

export interface SurveyConfigOption {
  value: AssessmentSurveyGroup;
  label: string;
  description: string;
  tag: string;
  tagVariant: "primaryFilled" | "neutralFilled";
  tagTooltip?: string;
  icon: IconName;
  surveys?: AssessmentSurveyType[];
}

interface AssessmentSurveyConfig {
  type: AssessmentSurveyType;
  group: AssessmentSurveyGroup;
}

export interface SurveyConfigSelectorProps {
  onChange?: (value: SurveyConfigOption) => void;
  surveysConfig?: AssessmentSurveyConfig[];
  showLabel?: boolean;
  description?: string;
  required?: boolean;
  className?: string;
  disabled?: boolean;
}

interface SurveyOptionCardProps {
  option: SurveyConfigOption;
  isSelected: boolean;
  disabled: boolean;
  onSelect: (value: AssessmentSurveyGroup) => void;
}

function SurveyOptionCard({
  option,
  isSelected,
  disabled,
  onSelect,
}: SurveyOptionCardProps) {
  const handleClick = useCallback(() => {
    if (!disabled) {
      onSelect(option.value);
    }
  }, [disabled, onSelect, option.value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === "Enter" || e.key === " ") && !disabled) {
        e.preventDefault();
        onSelect(option.value);
      }
    },
    [disabled, onSelect, option.value]
  );

  return (
    <div
      className={cn(
        "p-1 -mx-1 rounded-xl transition-colors",
        isSelected ? "bg-blue-100" : "bg-transparent"
      )}
    >
      <div
        role="radio"
        aria-checked={isSelected}
        tabIndex={disabled ? -1 : 0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          "flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-colors",
          "bg-white",
          isSelected ? "border-blue-500" : "border-grey-200",
          disabled && "opacity-50 cursor-not-allowed",
          !disabled && !isSelected && "hover:border-grey-300"
        )}
      >
        {/* Radio button */}
        <div
          className={cn(
            "flex h-4 w-4 items-center justify-center rounded-full border border-solid shrink-0",
            isSelected ? "border-primary" : "border-grey-300"
          )}
        >
          <div
            className={cn(
              "h-2 w-2 rounded-full transition-colors",
              isSelected ? "bg-primary" : "bg-transparent"
            )}
          />
        </div>

        {/* Icon */}
        <Icon icon={option.icon} size="md" />

        {/* Label and tag */}
        <Row className="items-center gap-4 flex-1">
          <span className="font-medium text-sm">{option.label}</span>

          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-pointer">
                <StatusBadge
                  label={option.tag}
                  variant={option.tagVariant}
                  className="border-none"
                />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{option.tagTooltip}</p>
            </TooltipContent>
          </Tooltip>
        </Row>

        {/* Description */}
        <span
          className={cn("text-xs text-gray-500", isSelected && "text-gray-600")}
        >
          {option.description}
        </span>
      </div>
    </div>
  );
}

const parseConfigSurveys = (
  group: AssessmentSurveyGroup,
  surveys?: AssessmentSurveyConfig[]
) => {
  const groupSurveys = filter(surveys, { group });

  return map(groupSurveys, (s) => s.type);
};

/**
 * SurveySourceSelector - A reusable widget for selecting survey version
 *
 */
function SurveyConfigSelector(props: SurveyConfigSelectorProps) {
  const {
    onChange,
    showLabel = true,
    description,
    required = false,
    className,
    surveysConfig,
    disabled = false,
  } = props;

  const [value, setValue] = useState<AssessmentSurveyGroup | undefined>();

  const { t } = useTranslation();

  const surveyConfigOptions: SurveyConfigOption[] = useMemo(
    () =>
      [
        {
          value: AssessmentSurveyGroup.AP,
          label: `${t("projects.ap_surveys")} (${t("common.recommended")})`,
          description: t("projects.ap_surveys_config"),
          tag: t("projects.tag_new"),
          tagVariant: "primaryFilled" as const,
          tagTooltip: t("projects.ap_surveys_notification_description"),
          icon: "APIcon" as const,
          surveys: parseConfigSurveys(AssessmentSurveyGroup.AP, surveysConfig),
        },
        {
          value: AssessmentSurveyGroup.WISNIO,
          label: t("projects.wisnio_surveys"),
          description: t("projects.wisnio_surveys_config"),
          tag: t("projects.tag_legacy"),
          tagVariant: "neutralFilled" as const,
          tagTooltip: t("projects.wisnio_surveys_notification_description"),
          icon: "WisnioIcon" as const,
          surveys: parseConfigSurveys(
            AssessmentSurveyGroup.WISNIO,
            surveysConfig
          ),
        },
      ].filter((c) => c.surveys?.length > 0),
    [t, surveysConfig]
  );

  const handleChange = useCallback(
    (option: SurveyConfigOption) => () => {
      setValue(option.value);
      onChange?.(option);
    },
    [onChange]
  );

  const labelText = t("projects.survey_version");

  useEffect(() => {
    if (disabled) return;
    if (surveyConfigOptions?.length === 1) {
      const onlyOption = surveyConfigOptions[0];
      if (!onlyOption) return;
      if (value === onlyOption.value) return;
      setValue(onlyOption.value);
      onChange?.(onlyOption);
    }
  }, [surveyConfigOptions, value, onChange, disabled]);

  if (surveyConfigOptions?.length < 2) return null;

  return (
    <Column
      className={cn("gap-4", className)}
      role="radiogroup"
      aria-label={labelText}
    >
      {showLabel && (
        <Column className="gap-1">
          <Label>
            {labelText}
            {required && " *"}
          </Label>
          <SubtitleText>{description}</SubtitleText>
        </Column>
      )}
      <Column className="gap-3">
        {surveyConfigOptions.map((option) => (
          <SurveyOptionCard
            key={option.value}
            option={option}
            isSelected={value === option.value}
            disabled={disabled}
            onSelect={handleChange(option)}
          />
        ))}
      </Column>
    </Column>
  );
}

SurveyConfigSelector.displayName = "SurveyConfigSelector";

export { SurveyConfigSelector };
